import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  Button,
  CircularProgress,
  Slider,
  TextField,
  Divider,
} from '@mui/material';
import { apiService, VectorIndex } from '../../services/api';
import { BEDROCK_MODELS, BedrockModel } from '../../constants/defaults';

interface RagSettingsPanelProps {
  editedRagVectorDb: string;
  editedRagTopK: number;
  editedRagConfidence: number;
  editedRagTokenLimit: number;
  editedBedrockModel: BedrockModel | '';
  onRagVectorDbChange: (value: string) => void;
  onRagTopKChange: (value: number) => void;
  onRagConfidenceChange: (value: number) => void;
  onRagTokenLimitChange: (value: number) => void;
  onBedrockModelChange: (value: BedrockModel) => void;
}

const RagSettingsPanel: React.FC<RagSettingsPanelProps> = ({
  editedRagVectorDb,
  editedRagTopK,
  editedRagConfidence,
  editedRagTokenLimit,
  editedBedrockModel,
  onRagVectorDbChange,
  onRagTopKChange,
  onRagConfidenceChange,
  onRagTokenLimitChange,
  onBedrockModelChange,
}) => {
  const [availableIndices, setAvailableIndices] = useState<string[]>([]);
  const [loadingIndices, setLoadingIndices] = useState(false);

  // Function to fetch vector indices from the RAG service
  const fetchVectorIndices = async () => {
    setLoadingIndices(true);
    try {
      const indices = await apiService.rag.listIndexes();
      console.log('Fetched indices:', indices);
      
      if (indices.length > 0) {
        const indexNames = indices.map((idx: VectorIndex) => idx.name);
        setAvailableIndices(indexNames);
        
        // If the current selected index is not in the list, clear it
        if (editedRagVectorDb && !indexNames.includes(editedRagVectorDb)) {
          onRagVectorDbChange('');
        }
      } else {
        setAvailableIndices([]);
      }
    } catch (error) {
      console.error('Error fetching vector indices:', error);
      setAvailableIndices([]);
    } finally {
      setLoadingIndices(false);
    }
  };

  // Fetch indices when component mounts
  useEffect(() => {
    fetchVectorIndices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" gutterBottom>RAG Settings</Typography>
      
      <TextField
        select
        label="Bedrock Model"
        fullWidth
        value={editedBedrockModel || ''}
        onChange={(e) => onBedrockModelChange(e.target.value as BedrockModel)}
        margin="normal"
      >
        {BEDROCK_MODELS.map((model) => (
          <MenuItem key={model.value} value={model.value}>
            {model.label}
          </MenuItem>
        ))}
      </TextField>
      
      <FormControl fullWidth margin="normal">
        <Typography variant="caption" sx={{ mb: 1 }}>
          Vector Database: {editedRagVectorDb || 'None selected'}
        </Typography>
        <Select
          value={availableIndices.includes(editedRagVectorDb) ? editedRagVectorDb : ''}
          onChange={(e) => {
            const newValue = e.target.value as string;
            console.log('Selected vector DB:', newValue);
            onRagVectorDbChange(newValue);
          }}
          displayEmpty
          renderValue={(value) => {
            if (!value) return <em>Select a vector database</em>;
            return <strong>{value}</strong>;
          }}
          endAdornment={
            <InputAdornment position="end" sx={{ pr: 0 }}>
              {loadingIndices ? (
                <CircularProgress size={20} />
              ) : (
                <Button
                  size="small"
                  onClick={fetchVectorIndices}
                  sx={{ color: '#1976d2', fontWeight: 500, mr: 2, minWidth: 'auto', px: 0.5 }}
                >
                  Refresh
                </Button>
              )}
            </InputAdornment>
          }
        >
          {availableIndices.length === 0 ? (
            <MenuItem key="no-indices" value="">
              <em>No indices available</em>
            </MenuItem>
          ) : (
            availableIndices.map((idx, index) => (
              <MenuItem key={`idx-${index}-${idx}`} value={idx}>
                {idx}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography id="top-k-slider" gutterBottom>
          Top-K Results: {editedRagTopK}
        </Typography>
        <Slider
          value={editedRagTopK}
          onChange={(_, value) => onRagTopKChange(value as number)}
          aria-labelledby="top-k-slider"
          step={1}
          marks
          min={1}
          max={10}
          valueLabelDisplay="auto"
        />
      </Box>
      
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography id="confidence-slider" gutterBottom>
          Confidence Threshold: {editedRagConfidence.toFixed(1)}
        </Typography>
        <Slider
          value={editedRagConfidence}
          onChange={(_, value) => onRagConfidenceChange(value as number)}
          aria-labelledby="confidence-slider"
          step={0.1}
          marks
          min={0}
          max={1}
          valueLabelDisplay="auto"
        />
      </Box>
      
      <TextField
        label="Token Limit"
        type="number"
        fullWidth
        value={editedRagTokenLimit}
        onChange={(e) => onRagTokenLimitChange(Number(e.target.value))}
        margin="normal"
        InputProps={{
          inputProps: { min: 100, max: 8000 }
        }}
      />
    </>
  );
};

export default RagSettingsPanel;
